import React, { useCallback, useState, useRef } from 'react';
import { useTheme } from '@/hooks/useTheme';
import { Button } from './Button';
import { BodyText } from './BodyText';
import { TextArea } from './TextArea';
import type { ReferralDocument } from '@/services/domains/ReferralDomain';
import { getLogger } from '@/services/shared/LoggerFactory';

export interface FileUploadProps {
  /** Function to handle file upload */
  onUpload: (file: File, description?: string) => Promise<ReferralDocument | null>;
  /** Function to validate file before upload */
  onValidate: (file: File) => { isValid: boolean; error?: string };
  /** Upload progress (0-100) */
  uploadProgress: {
    isUploading: boolean;
    progress: number;
    fileName?: string;
  };
  /** Accepted file types */
  acceptedTypes?: string[];
  /** Maximum file size in bytes */
  maxSize?: number;
  /** Whether upload is disabled */
  disabled?: boolean;
  /** Text for the upload button */
  uploadButtonText: string;
  /** Additional CSS classes */
  className?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onUpload,
  onValidate,
  uploadProgress,
  acceptedTypes = ['.pdf', '.doc', '.docx'],
  maxSize = 10 * 1024 * 1024, // 10MB
  disabled = false,
  uploadButtonText = "Upload File",
  className = '',
}) => {
  const logger = getLogger();
  const { isDark } = useTheme();
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle drag events
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  // Handle drop event
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled || uploadProgress.isUploading) return;

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelection(files[0]);
    }
  }, [disabled, uploadProgress.isUploading]);

  // Handle file selection
  const handleFileSelection = useCallback((file: File) => {
    setValidationError(null);

    // Validate file
    const validation = onValidate(file);
    if (!validation.isValid) {
      setValidationError(validation.error || 'Invalid file');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  }, [onValidate]);

  // Handle file input change
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      handleFileSelection(files[0]);
    }
  }, [handleFileSelection]);

  // Handle upload
  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;

    try {
      await onUpload(selectedFile, description.trim() || undefined);
      // Reset form on successful upload
      setSelectedFile(null);
      setDescription('');
      setValidationError(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      // Error handling is done in the parent component
      logger.error('Upload failed:', error);
    }
  }, [selectedFile, description, onUpload]);

  // Handle file removal
  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setDescription('');
    setValidationError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Format file size
  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* File Drop Zone */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
          dragActive
            ? isDark
              ? 'border-primary-400 bg-primary-900/20'
              : 'border-primary-400 bg-primary-50'
            : isDark
            ? 'border-secondary-600 hover:border-secondary-500'
            : 'border-secondary-300 hover:border-secondary-400'
        } ${
          disabled || uploadProgress.isUploading
            ? 'opacity-50 cursor-not-allowed'
            : 'cursor-pointer'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !disabled && !uploadProgress.isUploading && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={acceptedTypes.join(',')}
          onChange={handleFileInputChange}
          disabled={disabled || uploadProgress.isUploading}
        />

        <div className="text-center">
          <div className={`mx-auto h-12 w-12 ${isDark ? 'text-secondary-300' : 'text-secondary-600'}`}>
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
          </div>
          <div className="mt-4">
            <BodyText className={`${isDark ? 'text-secondary-300' : 'text-secondary-600'} font-medium`}>
              {uploadProgress.isUploading ? 'Uploading...' : 'Click to upload or drag and drop'}
            </BodyText>
            <BodyText variant="secondary" className="mt-1">
              {acceptedTypes.join(', ').toUpperCase()} up to {formatFileSize(maxSize)}
            </BodyText>
          </div>
        </div>
      </div>

      {/* Validation Error */}
      {validationError && (
        <div className={`p-3 rounded-md ${isDark ? 'bg-error-900/20 border border-error-800' : 'bg-error-50 border border-error-200'}`}>
          <BodyText className="text-error-600 text-sm">{validationError}</BodyText>
        </div>
      )}

      {/* Selected File */}
      {selectedFile && (
        <div className={`p-4 rounded-lg border ${
          isDark ? 'bg-secondary-800 border-secondary-700' : 'bg-secondary-50 border-secondary-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${
                isDark ? 'bg-primary-900 text-primary-200' : 'bg-primary-100 text-primary-800'
              }`}>
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <BodyText className="font-medium">{selectedFile.name}</BodyText>
                <BodyText variant="secondary" className="text-sm">
                  {formatFileSize(selectedFile.size)}
                </BodyText>
              </div>
            </div>
            <Button
              onClick={handleRemoveFile}
              variant="outline"
              size="sm"
              disabled={uploadProgress.isUploading}
              className="text-error-600 border-error-300 hover:bg-error-100 hover:border-error-500 hover:text-error-700 focus:ring-error-500"
            >
              Remove
            </Button>
          </div>

          {/* Description Input */}
          <div className="mt-4">
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-secondary-200' : 'text-secondary-700'
            }`}>
              Description (optional)
            </label>
            <TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add a description for this document..."
              rows={2}
              disabled={uploadProgress.isUploading}
            />
          </div>

          {/* Upload Progress */}
          {uploadProgress.isUploading && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <BodyText className="text-sm font-medium">
                  Uploading {uploadProgress.fileName}...
                </BodyText>
                <BodyText className="text-sm">{uploadProgress.progress}%</BodyText>
              </div>
              <div className={`w-full bg-secondary-200 rounded-full h-2 ${isDark ? 'bg-secondary-700' : ''}`}>
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress.progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Upload Button */}
          {!uploadProgress.isUploading && (
            <div className="mt-4">
              <Button
                onClick={handleUpload}
                disabled={!selectedFile || uploadProgress.isUploading}
                className="w-full"
              >
                {uploadButtonText}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
